'use client';

import React, { useState } from 'react';
import { ShoppingCart, MessageCircle, Check } from 'lucide-react';
import { useCart } from '@/context/CartContext';

interface ProductActionsClientProps {
  product: {
    title: string;
    partId?: string;
    shortId?: string;
    sku: string;
    category?: string;
    subcategory?: string;
    make?: string;
    model?: string;
    year?: string;
    trim?: string;
    engine?: string;
    seller?: string;
    sellerId?: string;
    images?: string[];
    condition?: string;
    stockQuantity?: number;
  };
  country: {
    code: string;
    slug: string;
    currency: string;
  };
  price: {
    amount: string | number;
    formatted: string;
  };
  canonical: string;
  whatsappUrl: string;
}

export default function ProductActionsClient({
  product,
  country,
  price,
  canonical,
  whatsappUrl,
}: ProductActionsClientProps) {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      const numPrice = typeof price.amount === 'number' ? price.amount : parseFloat(String(price.amount)) || 0;
      const cartProduct = {
        id: product.partId || `prod_${product.shortId}`,
        title: product.title,
        sku: product.sku,
        price: numPrice,
        currency: country.currency,
        imageUrl: product.images?.[0] || '',
        category: product.category,
        make: product.make,
        model: product.model,
        year: product.year,
        condition: product.condition,
        stockQuantity: product.stockQuantity || 1,
        seller: {
          id: product.sellerId,
          name: product.seller,
        },
      };

      const fitment = product.make ? {
        make: product.make,
        model: product.model,
        year: product.year,
      } : null;

      await addToCart(cartProduct, 1, fitment);
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 pt-1">
      {/* Primary Add to Cart Button */}
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={adding}
        className="flex-1 py-3.5 px-6 rounded-xl bg-[#0066FF] hover:bg-[#0052CC] active:scale-[0.99] text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-blue-500/20 transition-all cursor-pointer"
        title="Add to Parts4Drive Cart"
      >
        {added ? (
          <>
            <Check className="size-5 text-white" />
            <span>Added to Cart!</span>
          </>
        ) : (
          <>
            <ShoppingCart className="size-5 text-white" />
            <span>Add to Cart ({price.formatted})</span>
          </>
        )}
      </button>

      {/* Direct WhatsApp Order CTA */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 py-3.5 px-6 rounded-xl bg-[#25D366] hover:bg-[#20ba59] active:scale-[0.99] text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-emerald-500/20 transition-all cursor-pointer"
        title={`Order via WhatsApp from ${product.seller}`}
      >
        <MessageCircle className="size-5 fill-white text-[#25D366]" />
        <span>Order via WhatsApp</span>
      </a>
    </div>
  );
}
